import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@cynthai/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { twMerge } from "tailwind-merge";

import { addToast } from "@/core/lib/toast";
import type { OrganizationRead } from "@/openapi";
import {
  responseUserToInvitationMutation,
  getCurrentUserQueryKey,
  getCurrentUserInvitationsQueryKey,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

export default function InvitationCard({
  id,
  organization,
  className,
}: Readonly<{
  id: string;
  organization: OrganizationRead;
  className?: string;
}>) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const responseUserToInvitation = useMutation({
    ...responseUserToInvitationMutation(),
    onSuccess: async () => {
      // Invalidate user query to refresh user data
      queryClient.removeQueries({
        queryKey: getCurrentUserQueryKey(),
      });
      // Remove invitations from cache to ensure fresh load
      queryClient.removeQueries({
        queryKey: getCurrentUserInvitationsQueryKey(),
      });
      await navigate({ to: "/", replace: true });
    },
    onError: () => {
      addToast({
        title: m.toast_failed_to_respond_to_invitation(),
        description: m.toast_something_went_wrong_please_try_again(),
        severity: "danger",
      });
    },
  });

  return (
    <Card className={twMerge("w-fit text-text-primary", className)}>
      <CardHeader>{m.you_got_new_invite()}</CardHeader>

      <Divider />
      <CardBody>
        <p className="text-sm">
          {m.invited_to_org({ orgName: organization.name })}
        </p>
      </CardBody>
      <Divider />
      <CardFooter className="flex flex-row gap-4">
        <Button
          color="secondary"
          className="ml-auto"
          onPress={() =>
            responseUserToInvitation.mutate({
              path: {
                invitation_id: id,
                org_id: organization.id,
                decision: "decline",
              },
            })
          }
        >
          {m.decline_button_label()}
        </Button>
        <Button
          color="primary"
          isLoading={responseUserToInvitation.isPending}
          onPress={() =>
            responseUserToInvitation.mutate({
              path: {
                invitation_id: id,
                org_id: organization.id,
                decision: "accept",
              },
            })
          }
        >
          {m.accept_button_label()}
        </Button>
      </CardFooter>
    </Card>
  );
}
